'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import { CMSSection } from '@/components/cms/CmsSection';
import { Icon } from '@/components/ui/Icon';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useCallback } from 'react';
import styles from './PlaceholderPrint.module.scss';

interface PlaceholderPrintProps {
  /** Page title for the print heading (shown on mobile) */
  pageTitle?: string;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

/**
 * PlaceholderPrint - Print button component
 *
 * Renders a simple "Print this page" button that triggers the browser print dialog.
 * Shows a heading on mobile devices for context.
 *
 * @example
 * <PlaceholderPrint pageTitle="Watch Specifications" cmsIndex={0} />
 */
export function PlaceholderPrint({ pageTitle, cmsIndex, className }: PlaceholderPrintProps) {
  const aria = useAriaLabels();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const triggerPrint = useCallback(() => {
    window.print();
  }, []);

  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `placeholderprint-${cmsIndex}-${element}`
        : `placeholderprint-${cmsIndex}`
      : undefined;

  return (
    <CMSSection className={className} data-testid={testId()}>
      <div className={styles.wrapper} data-testid={testId('wrapper')}>
        {/* Heading shown only on mobile */}
        {isMobile && pageTitle && (
          <h2 className={styles.heading} data-testid={testId('heading')}>
            {pageTitle}
          </h2>
        )}

        <button
          type="button"
          className={styles.printButton}
          onClick={triggerPrint}
          aria-label={aria.action('printThisPage')}
          data-testid={testId('button')}
        >
          <Icon name="print" size={16} className={styles.icon} />
          <span className={styles.text}>Print this page</span>
        </button>
      </div>
    </CMSSection>
  );
}

PlaceholderPrint.displayName = 'PlaceholderPrint';
